<script lang="ts" setup>
  import { onMounted, onUnmounted, computed, watch, ref, onBeforeUnmount, useId } from 'vue';
  import { useViewParentLocation, useHttpClient, useDialogStack } from '@othercommerce/foundation';

  const open = defineModel('open', { type: Boolean, required: false, default: false });

  const props = defineProps({
    id: { type: String, required: false },
    visitable: { type: Boolean, required: false, default: false },
    topper: { type: Boolean, required: false, default: false },
    bottom: { type: Boolean, required: false, default: false },
    left: { type: Boolean, required: false, default: false },
    edge: { type: Boolean, required: false, default: false },
    sm: { type: Boolean, required: false, default: false },
    md: { type: Boolean, required: false, default: false },
    lg: { type: Boolean, required: false, default: false },
    xl: { type: Boolean, required: false, default: false },
  });

  const randomId = useId();
  const http = useHttpClient();
  const parent = useViewParentLocation();
  const stack = useDialogStack();

  const htmlModalElement = ref();

  const id = computed(() => {
    return props.id || randomId;
  });

  const typeClasses = computed(() => {
    return {
      'drawer--topper': props.topper,
      'drawer--bottom': props.bottom,
      'drawer--left': props.left,
      'drawer--edge': props.edge,
    };
  });

  const sizeClasses = computed(() => {
    return {
      'drawer__wrapper--sm': props.sm,
      'drawer__wrapper--md': props.md,
      'drawer__wrapper--lg': props.lg,
      'drawer__wrapper--xl': props.xl,
    };
  });

  function show() {
    htmlModalElement.value.showModal();
    stack.push(id.value);
  }

  function hide() {
    htmlModalElement.value.close();
    stack.pop(id.value);
  }

  function onCancel(event: Event) {
    if (props.visitable) {
      event.preventDefault();
      event.stopPropagation();

      if (parent.value) {
        http.get(parent.value);
      }
    }
  }

  function onPopstate() {
    hide();
  }

  onMounted(() => {
    if (open.value) {
      show();
    }
  });

  onBeforeUnmount(async () => {
    hide();
  });

  onMounted(() => {
    if (!props.visitable) {
      window.addEventListener('popstate', onPopstate);
    }
  });

  onUnmounted(() => {
    if (!props.visitable) {
      window.removeEventListener('popstate', onPopstate);
    }
  });

  // We need to watch for open model change.
  // Otherwise, the modal won't hide properly when model is changed from parent.
  watch(open, (value) => {
    if (value) {
      show();
    } else {
      hide();
    }
  });
</script>
<template>
  <dialog ref="htmlModalElement" :id="id" class="drawer" :class="typeClasses" v-bind="$attrs" @close="open = false" @keydown.esc="onCancel">
    <div class="drawer__container">
      <div class="drawer__wrapper" :class="sizeClasses">
        <div class="drawer__content">
          <slot />
        </div>
      </div>
    </div>
  </dialog>
</template>
<style>
  :root {
    --ui-drawer-wrapper-border-radius: calc(var(--section-border-radius) + 0.375rem);
    --ui-drawer-content-border-radius: var(--section-border-radius);
    --ui-drawer-backdrop: var(--backdrop-default);
    --ui-drawer-wrapper-background: rgb(255 255 255 / 0.6);
    --ui-drawer-wrapper-border-color: rgba(2, 28, 55, 0.08);
  }
</style>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .drawer {
    background: none;
    border: none;
    min-width: 100%;
    min-height: 100dvh;
    cursor: default;
    overflow: hidden auto;
    padding: 0;
    margin: 0;
    top: 0;
    left: 0;

    &__container {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: flex-end;
      padding: 1rem;
      min-height: 100dvh;
      min-width: 0;
      width: 100%;
    }

    &__wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background: var(--ui-drawer-wrapper-background);
      border: 1px solid var(--ui-drawer-wrapper-border-color);
      backdrop-filter: blur(2px);
      border-radius: var(--ui-drawer-wrapper-border-radius);
      padding: 0.375rem;
      min-height: calc(100dvh - 2rem);
      max-width: 425px;
      min-width: 0;
      width: 100%;


      &.drawer__wrapper--sm {
        max-width: 538px;
      }

      &.drawer__wrapper--md {
        max-width: 640px;
      }

      &.drawer__wrapper--lg {
        max-width: 768px;
      }

      &.drawer__wrapper--xl {
        max-width: 1024px;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      container-type: inline-size;
      container-name: drawer-content;
      overflow: hidden;
      flex: 1 1 auto;
      background: #fff;
      box-shadow: 0 0 2px 0 rgb(0 0 0 / 0.25), 0 25px 50px -12px rgb(0 0 0 / 0.32);
      border-radius: var(--ui-drawer-content-border-radius);
      padding: 2rem;
      min-height: 100%;
      min-width: 0;
      width: 100%;
      gap: 1rem;
    }


    &.drawer--topper,
    &.drawer--bottom {
      .drawer__container {
        .drawer__wrapper {
          min-height: 0;
          max-width: 100%;
        }
      }
    }

    &.drawer--topper {
      .drawer__container {
        align-items: flex-start;
        justify-content: flex-start;

      }
    }

    &.drawer--bottom {
      .drawer__container {
        align-items: flex-end;
        justify-content: flex-end;
      }
    }

    &.drawer--left {
      .drawer__container {
        align-items: flex-start;
        justify-content: flex-start;
      }
    }

    &.drawer--edge {
      .drawer__container {
        align-items: flex-start;
        justify-content: flex-start;
        padding: 0;

        .drawer__wrapper {
          border-radius: 0;
          border-width: 0 1px 0 0;
          padding: 0 0.375rem 0 0;
          min-height: 100dvh;

          .drawer__content {
            border-radius: 0;
          }
        }
      }
    }

    @include media.query(md) {

      &__container,
      &.drawer--left .drawer__container,
      &.drawer--edge .drawer__container,
      &.drawer--topper .drawer__container {
        align-items: flex-end;
        justify-content: flex-end;
        padding: 0;
      }

      &.drawer--edge .drawer__container .drawer__wrapper {
        min-height: unset
      }

      &__wrapper {
        min-height: unset;
        padding: 0;
        border: none;
        backdrop-filter: none;
        max-width: unset;
        width: 100%;

        &.drawer__wrapper--sm,
        &.drawer__wrapper--md,
        &.drawer__wrapper--lg,
        &.drawer__wrapper--xl {
          max-width: 100%;
        }
      }

      &__content {
        padding: 1rem 1rem 1.75rem;
        box-shadow: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        gap: 1.5rem;
      }
    }
  }

  dialog {
    color: var(--color-default, #000);
    opacity: 0;
    transform: translateX(50%);
    transition-property: opacity, transform, overlay, display;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    transition-behavior: allow-discrete;

    &.drawer--left {
      transform: translateX(-50%);
    }

    &.drawer--topper {
      transform: translate(0, -50%);
    }

    &.drawer--bottom {
      transform: translate(0, 50%);
    }
  }

  dialog[open] {
    opacity: 1;
    transform: translate(0);
  }

  @starting-style {
    dialog[open] {
      opacity: 0;
      transform: translateX(50%);

      &.drawer--left {
        transform: translateX(-50%);
      }

      &.drawer--topper {
        transform: translate(0, -50%);
      }

      &.drawer--bottom {
        transform: translate(0, 50%);
      }
    }
  }

  dialog::backdrop {
    background-color: transparent;
    transition-property: background-color, overlay, display;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    transition-behavior: allow-discrete;
  }

  dialog[open]::backdrop {
    background-color: var(--ui-drawer-backdrop)
  }

  @starting-style {
    dialog[open]::backdrop {
      background-color: transparent;
    }
  }

  @include media.query(md) {

    dialog,
    dialog.drawer--left,
    dialog.drawer--topper,
    dialog.drawer--bottom {
      transform: translateY(50%);
    }

    dialog[open],
    dialog[open].drawer--left,
    dialog[open].drawer--topper,
    dialog[open].drawer--bottom {
      transform: translateY(0);
    }

    @starting-style {
      dialog[open],
      dialog[open].drawer--left,
      dialog[open].drawer--topper,
      dialog[open].drawer--bottom {
        transform: translateY(50%);
      }
    }
  }
</style>
